import React, { useState, useEffect } from 'react';
import { habitService } from '../services/habitService';
import { FaPlus, FaTimes, FaCheck, FaUndo, FaTrash, FaStar, FaBan } from 'react-icons/fa';
import './Habits.css';

const HabitForm = ({ onSubmit, onCancel, initialValues }) => {
  const [formData, setFormData] = useState(initialValues);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="add-form">
      <input
        type="text"
        placeholder="Habit Name"
        value={formData.name}
        onChange={(e) => setFormData({...formData, name: e.target.value})}
        required
      />
      <select
        value={formData.type}
        onChange={(e) => setFormData({...formData, type: e.target.value})}
        required
      >
        <option value="good">Good Habit</option>
        <option value="bad">Bad Habit</option>
      </select>
      <select
        value={formData.target}
        onChange={(e) => setFormData({...formData, target: e.target.value})}
        required
      >
        <option value="daily">Daily</option>
        <option value="weekly">Weekly</option>
        <option value="reduce">Reduce/Quit</option>
      </select>
      <textarea
        placeholder="Notes"
        value={formData.notes}
        onChange={(e) => setFormData({...formData, notes: e.target.value})}
      />
      <div className="form-actions">
        <button type="submit">Save Habit</button>
        <button type="button" onClick={onCancel} className="cancel-button">Cancel</button>
      </div>
    </form>
  );
};

const HabitCard = ({ habit, onUpdateStreak, onDelete }) => (
  <div className={`habit-card ${habit.type}`}>
    <div className="habit-header">
      <h3>
        {habit.type === 'good' ? <FaStar className="header-icon" /> : <FaBan className="header-icon" />}
        {habit.name}
      </h3>
    </div>
    <p>{habit.type === 'good' ? 'Current Streak:' : 'Days Without:'} {habit.streak} days</p>
    <p>Target: {habit.target}</p>
    <p className="notes">{habit.notes}</p>
    <div className="habit-actions">
      <button 
        onClick={() => onUpdateStreak(habit._id, true)} 
        className="streak-button increase"
        title={habit.type === 'good' ? 'Completed Today' : 'Stayed Strong'}
      >
        <FaCheck className="action-icon" />
      </button>
      <button 
        onClick={() => onUpdateStreak(habit._id, false)} 
        className="streak-button reset"
        title="Reset"
      >
        <FaUndo className="action-icon" />
      </button>
      <button 
        onClick={() => onDelete(habit._id)} 
        className="delete-button"
        title="Delete"
      >
        <FaTrash className="action-icon" />
      </button>
    </div>
  </div>
);

function Habits() {
  const [showForm, setShowForm] = useState(false);
  const [habits, setHabits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const initialHabitState = {
    name: '',
    type: 'good',
    streak: 0,
    lastTracked: new Date().toISOString().split('T')[0],
    target: 'daily',
    notes: ''
  };

  useEffect(() => {
    loadHabits();
  }, []);

  const loadHabits = async () => {
    try {
      setLoading(true);
      const response = await habitService.getHabits();
      setHabits(response.data);
      setError(null);
    } catch (error) {
      setError('Failed to load habits. Please try again.');
      console.error('Error loading habits:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (habitData) => {
    try {
      await habitService.createHabit(habitData);
      await loadHabits();
      setShowForm(false);
    } catch (error) {
      setError('Failed to create habit. Please try again.');
      console.error('Error creating habit:', error);
    }
  };

  const handleUpdateStreak = async (habitId, increase) => {
    try {
      const habit = habits.find(h => h._id === habitId);
      if (!habit) return;
      
      const updatedStreak = increase ? habit.streak + 1 : 0;
      await habitService.updateHabit(habitId, {
        ...habit,
        streak: updatedStreak,
        lastTracked: new Date().toISOString().split('T')[0]
      });
      await loadHabits();
    } catch (error) {
      setError('Failed to update habit. Please try again.');
      console.error('Error updating habit:', error);
    }
  };

  const handleDeleteHabit = async (habitId) => {
    if (!window.confirm('Are you sure you want to delete this habit?')) return;
    try {
      await habitService.deleteHabit(habitId);
      await loadHabits();
    } catch (error) {
      setError('Failed to delete habit. Please try again.');
      console.error('Error deleting habit:', error);
    }
  };

  if (loading) return <div className="loading">Loading habits...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="habits-container">
      <div className="header-actions">
        <h1>Habits Tracker</h1>
        <button onClick={() => setShowForm(!showForm)} className="add-button">
          {showForm ? <FaTimes /> : <FaPlus />}
          <span className="ml-2">{showForm ? 'Cancel' : 'Add New Habit'}</span>
        </button>
      </div>

      {showForm && (
        <HabitForm
          onSubmit={handleSubmit}
          onCancel={() => setShowForm(false)}
          initialValues={initialHabitState}
        />
      )}

      <div className="habits-grid">
        {['good', 'bad'].map(type => (
          <div key={type} className="habits-section">
            <h2>{type === 'good' ? 'Good Habits' : 'Bad Habits'}</h2>
            <div className="habits-list">
              {habits
                .filter(habit => habit.type === type)
                .map(habit => (
                  <HabitCard
                    key={habit._id}
                    habit={habit}
                    onUpdateStreak={handleUpdateStreak}
                    onDelete={handleDeleteHabit}
                  />
                ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default Habits;
